import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Lock icon for gated content
const LockIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// Download icon
const DownloadIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

// Microphone Icon
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

const IssueDetail = () => {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      setPurchaseEmail(response.data.email);
    } catch (error) {
      setCurrentUser(null);
    }
  }, []);

  const fetchIssue = useCallback(async () => {
    try {
      const response = await api.get(`/magazine/issues/${slug}`);
      setIssue(response.data);
      
      // Check access if user is logged in
      if (currentUser?.email) {
        checkAccess(response.data.issue_id, currentUser.email);
      }
    } catch (error) {
      console.error("Failed to fetch issue:", error);
      toast.error("Issue not found");
      navigate("/magazine");
    } finally {
      setLoading(false);
    }
  }, [slug, currentUser, navigate]);

  useEffect(() => {
    checkAuth();
    fetchIssue();
  }, [slug, checkAuth, fetchIssue]);

  const checkAccess = async (issueId, email) => {
    try {
      const response = await api.post(`/magazine/verify-purchase?issue_id=${issueId}&user_email=${email}`);
      setHasAccess(response.data.has_access);
    } catch (error) {
      setHasAccess(false);
    }
  };

  // Re-check access when user changes or issue loads
  useEffect(() => {
    if (issue && currentUser?.email) {
      checkAccess(issue.issue_id, currentUser.email);
      // Also set purchase email when user is loaded (guarded so we don't
      // re-set every render — no dependency loop).
      if (!purchaseEmail) {
        setPurchaseEmail(currentUser.email);
      }
    }
  }, [currentUser, issue, purchaseEmail]);

  const handleBuyIssue = async () => {
    // Use logged-in user's email or the one from state
    const emailToUse = currentUser?.email || purchaseEmail;
    
    // If no email yet, show input
    if (!emailToUse) {
      setShowEmailInput(true);
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await api.post('/magazine/checkout', {
        issue_id: issue.issue_id,
        user_email: emailToUse,
        origin_url: window.location.origin
      });

      if (response.data.has_access) {
        toast.success("You already own this issue!");
        setHasAccess(true);
      } else if (response.data.url) {
        // Redirect to Stripe
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (purchaseEmail) {
      setShowEmailInput(false);
      handleBuyIssue();
    }
  };

  const handleDownload = async () => {
    if (!issue || !purchaseEmail) {
      toast.error("Please enter your purchase email to download");
      return;
    }

    setDownloading(true);
    try {
      const response = await api.get(
        `/magazine/download/${issue.issue_id}`,
        {
          params: { user_email: purchaseEmail },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${issue.slug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      toast.error("Download failed", {
        description: error.response?.data?.detail || "Purchase required"
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="magazine" />
        <div className="pt-24 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading issue...</p>
        </div>
      </div>
    );
  }

  if (!issue) return null;

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="issue-detail-page">
      <SharedNavigation currentUser={currentUser} activePage="magazine" />

      {/* Back Button */}
      <div className="pt-20 px-4">
        <div className="max-w-7xl mx-auto py-6">
          <Link to="/magazine">
            <Button variant="ghost" className="text-purple-400 hover:text-purple-300">
              ← Back to Magazine
            </Button>
          </Link>
        </div>
      </div>

      {/* Issue Details */}
      <section className="px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Cover Image */}
            <div className="sticky top-24" data-testid="issue-cover">
              <Card className="overflow-hidden bg-gray-900/50 border-purple-900/30">
                <img
                  src={`${API}/magazine/public/cover/${issue.cover_image}`}
                  alt={issue.title}
                  className="w-full h-auto"
                />
              </Card>
            </div>

            {/* Issue Info */}
            <div>
              {/* Access Badge */}
              {hasAccess && (
                <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                  <span className="text-green-400 text-sm font-medium">✓ You own this issue</span>
                </div>
              )}

              <h1 className="text-4xl font-bold text-white mb-4" data-testid="issue-title">
                {issue.title}
              </h1>
              
              <p className="text-gray-400 mb-4" data-testid="issue-date">
                {new Date(issue.issue_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })}
              </p>
              
              <p className="text-gray-300 text-lg mb-6 leading-relaxed" data-testid="issue-description">
                {issue.description}
              </p>

              {/* Featured Artists */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-4">Inside This Issue</h3>
                <div className="space-y-2">
                  {issue.featured_artists.filter(a => a.name).map((artist, idx) => (
                    <div key={idx} className="flex items-center gap-3" data-testid={`featured-artist-${idx}`}>
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-yellow-500"></div>
                      <span className="text-gray-300">
                        <span className="font-semibold text-white">{artist.name}</span>
                        {artist.category && (
                          <>
                            <span className="text-gray-500 mx-2">•</span>
                            <span className="text-purple-400">{artist.category}</span>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="flex items-center gap-4 mb-6">
                <div className="text-3xl font-bold text-yellow-400" data-testid="issue-price">
                  ${issue.price.toFixed(2)}
                </div>
              </div>

              {/* Purchase/Access Actions */}
              {hasAccess ? (
                <Card className="p-6 bg-purple-900/20 border-purple-500/30 mb-6">
                  <h4 className="text-lg font-bold text-white mb-4">Your Purchase</h4>
                  <div className="flex flex-wrap gap-4">
                    <Button
                      onClick={handleDownload}
                      disabled={downloading}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                      data-testid="download-pdf-btn"
                    >
                      <DownloadIcon className="w-5 h-5 mr-2" />
                      {downloading ? "Downloading..." : "Download PDF"}
                    </Button>
                  </div>
                </Card>
              ) : (
                <div className="space-y-4 mb-6">
                  {showEmailInput ? (
                    <Card className="p-6 bg-gray-900/50 border-purple-900/30">
                      <form onSubmit={handleEmailSubmit}>
                        <label className="block text-white font-medium mb-2">
                          Enter your email to purchase
                        </label>
                        <div className="flex gap-3">
                          <Input
                            type="email"
                            value={purchaseEmail}
                            onChange={(e) => setPurchaseEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                            className="flex-1 bg-gray-800 border-gray-700 text-white"
                            data-testid="email-input"
                          />
                          <Button
                            type="submit"
                            disabled={checkoutLoading}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                          >
                            {checkoutLoading ? "Processing..." : "Continue"}
                          </Button>
                        </div>
                        <p className="text-gray-500 text-sm mt-2">
                          We'll use this email for your purchase receipt and access
                        </p>
                      </form>
                    </Card>
                  ) : (
                    <Button
                      onClick={handleBuyIssue}
                      disabled={checkoutLoading}
                      className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-8 py-6 text-lg"
                      data-testid="buy-issue-btn"
                    >
                      {checkoutLoading ? "Processing..." : `Buy Now - $${issue.price.toFixed(2)}`}
                    </Button>
                  )}
                </div>
              )}

              {/* Teaser Content (visible to all) */}
              {issue.teaser_content && (
                <Card className="p-6 bg-gray-900/50 border-purple-900/30 mb-6">
                  <h4 className="text-lg font-bold text-white mb-3">Preview</h4>
                  <p className="text-gray-300 whitespace-pre-line">{issue.teaser_content}</p>
                </Card>
              )}

              {/* Gated Content Notice */}
              {!hasAccess && (
                <Card className="p-6 bg-gray-900/30 border-gray-800 mb-6">
                  <div className="flex items-center gap-3 text-gray-400">
                    <LockIcon className="w-6 h-6" />
                    <span>Purchase this issue to access the full PDF and exclusive content</span>
                  </div>
                </Card>
              )}

              {/* Submit Music CTA */}
              <Link to="/login">
                <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20">
                  Submit Your Music to RGM Studio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Pages */}
      <section className="px-4 py-12 bg-black/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8">Preview Pages</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {issue.preview_images.map((preview, idx) => (
              <Card 
                key={idx} 
                className="overflow-hidden bg-gray-900/50 border-purple-900/30 hover:border-purple-500/50 transition-colors"
                data-testid={`preview-${idx}`}
              >
                <img
                  src={`${API}/magazine/public/preview/${preview}`}
                  alt={`Preview ${idx + 1}`}
                  className="w-full h-auto"
                />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-900/30 bg-black/80 backdrop-blur-xl py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/magazine" className="hover:text-purple-400 transition-colors">
                Magazine
              </Link>
              <Link to="/explore" className="hover:text-purple-400 transition-colors">
                Explore
              </Link>
              <Link to="/community-guidelines" className="hover:text-purple-400 transition-colors">
                Guidelines
              </Link>
            </div>
            <p className="text-gray-500 text-sm">© 2025 RGM Studio™</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IssueDetail;
